import { useState } from "react";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import {
  Container,
  Eyebrow,
  SectionLabel,
  AccentButton,
  SurfaceCard,
} from "@/components/shared/Primitives";
import { RadarRings } from "@/components/shared/Decorations";
import { BrowserMock } from "@/components/shared/AppMocks";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { SkyScene } from "@/components/shared/SkyScene";
import { APPS_FEATURED, APPS_CATALOG, APP_INCLUDES, STATUS_COLORS, tint } from "@/data/site";
import { useTheme } from "@/context/ThemeProvider";

const slug = (n) => n.toLowerCase().replace(/[^a-z0-9]/g, "");

// Real app screenshots for the featured showcases (keyed by app name).
const FEATURED_SHOTS = {
  "StrategyMap": "/app-strategymap.png",
  "MSS Operations": "/app-mssops.png",
  "Afterglow": "/app-afterglow.png",
};

// Browser-styled chrome wrapper for real screenshots.
const MockDots = () => (
  <div className="flex gap-1.5">
    {["#FF6B5C", "#FFC24B", "#3FCF8E"].map((c, i) => (
      <span key={i} className="h-2.5 w-2.5 rounded-full" style={{ background: c, opacity: 0.85 }} />
    ))}
  </div>
);

const ScreenshotMock = ({ src, alt, url, h, loading = "lazy" }) => (
  <div className="overflow-hidden rounded-[14px]" style={{ background: "linear-gradient(155deg,#0C1A2C,#060D18)", border: "1px solid var(--stroke-1)", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}>
    <div className="flex items-center gap-3 border-b px-4 py-3" style={{ borderColor: "rgba(231,238,248,0.10)" }}>
      <MockDots />
      <div className="ml-1 flex-1 truncate rounded-md px-3 py-1.5 text-[10px]" style={{ background: "rgba(255,255,255,0.04)", color: "rgba(231,238,248,0.42)", fontFamily: "'IBM Plex Mono', monospace" }}>{url}</div>
    </div>
    <div className="overflow-hidden p-4 sm:p-5" style={{ height: h }}>
      <img src={src} alt={alt} loading={loading} className="h-full w-full rounded-[8px] object-cover" />
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] || "#6FE7FF";
  return (
    <span
      className="font-tech inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.12em]"
      style={{ background: `${c}1f`, color: c, border: `1px solid ${c}55` }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
      {status}
    </span>
  );
};

// Sky-themed icon container: frosted cloud in light mode, glowing cosmic in dark.
const SkyIcon = ({ accent, size = "h-11 w-11", radius = "rounded-[12px]", light, children }) => (
  <span
    className={`grid flex-shrink-0 place-items-center transition-shadow duration-300 ${size} ${radius}`}
    style={light
      ? { background: "rgba(255,255,255,0.92)", color: accent, border: "1px solid rgba(150,210,255,0.55)", boxShadow: "0 2px 16px rgba(100,180,255,0.28), 0 0 0 1.5px rgba(255,255,255,0.85)" }
      : { background: `${accent}18`, color: accent, border: `1px solid ${accent}30`, boxShadow: `0 0 22px ${accent}40, inset 0 0 8px ${accent}0e` }
    }
  >
    {children}
  </span>
);

export default function Apps() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const { theme } = useTheme();
  const light = theme !== "dark";

  const join = (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error("Enter a valid email address"); return; }
    setDone(true); setEmail(""); toast.success("You're on the waitlist \u2014 see you soon.");
    setTimeout(() => setDone(false), 2600);
  };

  return (
    <div className="relative overflow-hidden">
      <SkyScene mode={light ? "day" : "night"} />

      <div className="relative z-10">
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute -right-48 -top-40 z-[1] h-[640px] w-[640px] opacity-25" style={{ color: "var(--page-accent)" }}>
            <RadarRings className="h-full w-full" />
          </div>
          <Container className="relative z-10 pb-16 pt-16 lg:pb-20 lg:pt-24">
            <div>
              <Reveal><Eyebrow>The Apps — a growing collection</Eyebrow></Reveal>
              <Reveal delay={0.05}>
                <h1 className="font-display mt-7" style={{ fontSize: "clamp(46px, 7vw, 100px)", lineHeight: 0.92, letterSpacing: "0.02em", color: "var(--text-0)" }}>
                  OWN THE<br /><span className="glow-accent" style={{ color: "var(--page-accent)" }}>CRAFT</span> YOURSELF.
                </h1>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-7 max-w-md text-[15px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>
                  A whole studio of software for in-house teams — from the all-in-one workspace to
                  tiny single-purpose tools. Write, build, test and measure, without the agency overhead.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <AccentButton href="#catalog" data-testid="apps-hero-cta">Browse all apps</AccentButton>
                  <AccentButton href="#waitlist" variant="outline">Get early access</AccentButton>
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3">
                  {APP_INCLUDES.map((it) => {
                    const Icon = it.icon;
                    return (
                      <span key={it.label} className="inline-flex items-center gap-2 text-[12px]" style={{ color: "var(--text-2)" }}>
                        <Icon className="h-4 w-4" style={{ color: "var(--page-accent)" }} />
                        {it.label}
                      </span>
                    );
                  })}
                </div>
              </Reveal>
            </div>
          </Container>
        </section>

        {/* FEATURED SHOWCASES */}
        <section className="py-16 lg:py-24">
          <Container>
            <SectionLabel index="01" total="02">Featured</SectionLabel>
            <div className="space-y-20 lg:space-y-28">
              {APPS_FEATURED.map((app, i) => {
                const Icon = app.icon;
                const odd = i % 2 === 1;
                return (
                  <Reveal key={app.name}>
                    <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
                      <div className={odd ? "lg:order-2" : ""}>
                        <div className="flex items-center gap-3">
                          <SkyIcon accent={app.accent} light={light}>
                            <Icon className="h-5 w-5" />
                          </SkyIcon>
                          <span className="font-tech text-[11px] uppercase tracking-[0.16em]" style={{ color: app.accent }}>{app.category}</span>
                          <StatusBadge status={app.status} />
                        </div>
                        <h3 className="font-display mt-5" style={{ fontSize: "clamp(34px, 4.4vw, 56px)", lineHeight: 0.96, letterSpacing: "0.01em", color: "var(--text-0)" }}>
                          {app.name}
                        </h3>
                        <p className="mt-4 max-w-md text-[15px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>{app.desc}</p>
                        <ul className="mt-6 space-y-2.5">
                          {app.points.map((p) => (
                            <li key={p} className="flex items-center gap-3 text-[14px]" style={{ color: "var(--text-1)" }}>
                              <span
                                className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-full"
                                style={light
                                  ? { background: "rgba(255,255,255,0.90)", color: app.accent, border: `1px solid ${app.accent}55`, boxShadow: "0 2px 8px rgba(100,180,255,0.22)" }
                                  : { background: `${app.accent}22`, color: app.accent }
                                }
                              >
                                <Check className="h-3 w-3" />
                              </span>
                              {p}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-8">
                          <AccentButton href="#waitlist">Explore {app.name}</AccentButton>
                        </div>
                      </div>
                      <div className={odd ? "lg:order-1" : ""}>
                        <ScreenshotMock
                          src={FEATURED_SHOTS[app.name]}
                          alt={`${app.name} app interface`}
                          url={`${slug(app.name)}.emailink.io`}
                          h={260}
                        />
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </Container>
        </section>

        {/* FULL CATALOG */}
        <section id="catalog" className="scroll-mt-24 py-16 lg:py-24">
          <Container>
            <SectionLabel index="02" total="02">The full catalog</SectionLabel>
            <Reveal>
              <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
                <h2 className="max-w-xl text-2xl font-semibold leading-tight sm:text-3xl" style={{ color: "var(--text-0)" }}>
                  Small, sharp tools for every corner of retention.
                </h2>
                <span className="font-tech text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--text-3)" }}>
                  {APPS_CATALOG.length} apps · more shipping monthly
                </span>
              </div>
            </Reveal>
            <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {APPS_CATALOG.map((app) => {
                const Icon = app.icon;
                return (
                  <StaggerItem key={app.name}>
                    <SurfaceCard
                      className="group flex h-full flex-col"
                      style={light
                        ? { background: "rgba(255,255,255,0.82)", border: "1px solid rgba(150,210,255,0.38)", boxShadow: "0 4px 24px rgba(80,160,255,0.10), 0 1px 0 rgba(255,255,255,0.95)" }
                        : { background: "rgba(8,20,46,0.62)", border: "1px solid rgba(120,160,230,0.18)", boxShadow: "0 8px 30px rgba(0,0,0,0.45)" }
                      }
                    >
                      <BrowserMock variant={app.variant} accent={app.accent} h={140} url={`${slug(app.name)}.emailink.studio`} />
                      <div className="mt-5 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                          <SkyIcon accent={app.accent} size="h-9 w-9" radius="rounded-[10px]" light={light}>
                            <Icon className="h-4 w-4" />
                          </SkyIcon>
                          <h3 className="text-[16px] font-semibold" style={{ color: "var(--text-0)" }}>{app.name}</h3>
                        </div>
                        <StatusBadge status={app.status} />
                      </div>
                      <span className="font-tech mt-3 inline-block text-[10px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>{app.category}</span>
                      <p className="mt-2.5 flex-1 text-[13px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>{app.desc}</p>
                      <a href="#waitlist" className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] transition-transform group-hover:translate-x-0.5" style={{ color: app.accent }}>
                        View app <ArrowUpRight className="h-4 w-4" />
                      </a>
                    </SurfaceCard>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </Container>
        </section>

        {/* WAITLIST CTA */}
        <section id="waitlist" className="scroll-mt-24 py-24">
          <Container>
            <Reveal>
              <div
                className="relative overflow-hidden rounded-[24px] p-9 text-center sm:p-16"
                style={light
                  ? { background: "rgba(255,255,255,0.78)", border: "1px solid rgba(150,210,255,0.50)", boxShadow: "0 8px 48px rgba(80,160,255,0.14), inset 0 1px 0 rgba(255,255,255,0.95)" }
                  : { background: "rgba(8,20,46,0.62)", border: `1px solid ${tint(24)}`, boxShadow: "0 12px 48px rgba(0,0,0,0.5)" }
                }
              >
                <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 opacity-40" style={{ color: "var(--page-accent)" }}>
                  <RadarRings className="h-full w-full" />
                </div>
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-30"
                  style={{ background: light
                    ? "radial-gradient(circle, rgba(255,220,80,0.70) 0%, rgba(255,180,40,0.25) 45%, transparent 70%)"
                    : "radial-gradient(circle, rgba(100,160,255,0.55) 0%, rgba(60,100,200,0.20) 45%, transparent 70%)"
                  }}
                />
                <Eyebrow className="justify-center">Early access</Eyebrow>
                <h2 className="font-display mx-auto mt-6 max-w-2xl" style={{ fontSize: "clamp(36px, 5vw, 68px)", lineHeight: 0.96, letterSpacing: "0.02em", color: "var(--text-0)" }}>
                  BE FIRST IN THE INBOX.
                </h2>
                <p className="mx-auto mt-5 max-w-md text-[15px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>
                  Join the waitlist and we'll send your invite the moment a new app opens up.
                </p>
                <form onSubmit={join} className="mx-auto mt-8 flex max-w-md items-center gap-2">
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@brand.com"
                    data-testid="apps-waitlist-input"
                    className="h-12 w-full rounded-[12px] px-4 text-[14px] outline-none"
                    style={{ background: "var(--input-bg)", border: "1px solid var(--stroke-1)", color: "var(--text-0)" }}
                  />
                  <button type="submit" data-testid="apps-waitlist-submit" className="flex h-12 flex-shrink-0 items-center gap-2 rounded-[12px] px-5 text-[12px] font-semibold uppercase tracking-[0.08em] transition-transform active:scale-95" style={{ background: "var(--page-accent)", color: "var(--on-accent)" }}>
                    {done ? <Check className="h-4 w-4" /> : <>Join <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </form>
              </div>
            </Reveal>
          </Container>
        </section>
      </div>
    </div>
  );
}
